import { z } from "zod";
/**
 * Typed custom properties on items and containers (ADR-0037). Storage is open
 * (`Record<string, PropertyValue>`); the UI edits a closed vocabulary of known
 * date semantics below. `metadata` stays the provider-owned bag.
 */
export const PROPERTY_TYPES = ["date"];
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
/**
 * True for a real day-precision calendar date in `YYYY-MM-DD` form. Rejects
 * both the wrong shape and impossible dates such as `2026-02-31`.
 *
 * @param value candidate date string.
 * @returns true when the string is a valid calendar day.
 */
export function isIsoDate(value) {
    if (!ISO_DATE_PATTERN.test(value))
        return false;
    const [year, month, day] = value.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    return (date.getFullYear() === year &&
        date.getMonth() === month - 1 &&
        date.getDate() === day);
}
/** A validated `YYYY-MM-DD` day-precision date value (ADR-0037). */
export const dateValueSchema = z
    .string()
    .refine(isIsoDate, { message: "Expected a day-precision date (YYYY-MM-DD)" });
/** One typed property; `type` is the discriminator (only `date` today). */
export const datePropertySchema = z.object({
    type: z.literal("date"),
    value: dateValueSchema,
});
export const propertySchema = z.discriminatedUnion("type", [datePropertySchema]);
/** Open key bag with typed values; declared on both item and container schemas. */
export const propertiesSchema = z.record(propertySchema);
/** Item-list sort modes shared by the API query signatures and the client. */
export const ITEM_SORTS = ["name", "newest", "category", "soonest"];
export const itemSortSchema = z.enum(ITEM_SORTS);
// ── Closed UI vocabulary of date semantics (ADR-0037) ─────────────────
/** Deadlines run out: they warn, sort and filter. */
export const ITEM_DEADLINE_KEYS = [
    "bestBefore",
    "returnBy",
    "expiresOn",
    "warrantyUntil",
];
/** Reference dates only record something that happened (never urgent). */
export const ITEM_REFERENCE_DATE_KEYS = ["purchasedOn"];
/** Every date key the item UI can edit. */
export const ITEM_DATE_KEYS = [
    ...ITEM_DEADLINE_KEYS,
    ...ITEM_REFERENCE_DATE_KEYS,
];
/** Containers carry expires-on only (ticket 03 adopts the same storage). */
export const CONTAINER_DEADLINE_KEYS = ["expiresOn"];
export const ITEM_DEADLINE_WARNING_DAYS = 7;
/** A month, because a seven-day warning is too late to claim a warranty. */
export const WARRANTY_WARNING_DAYS = 30;
export const CONTAINER_DEADLINE_WARNING_DAYS = 28;
/**
 * Warning window for one item deadline: warranties warn a month ahead, every
 * other item deadline a week (ADR-0037).
 *
 * @param key known item deadline key.
 * @returns days before the deadline at which the row turns amber.
 */
export function itemDeadlineThresholdDays(key) {
    return key === "warrantyUntil" ? WARRANTY_WARNING_DAYS : ITEM_DEADLINE_WARNING_DAYS;
}
// ── Reading properties ───────────────────────────────────────────────
/**
 * A property's date value when it is present and typed as a date.
 *
 * @param properties item/container property bag (may be undefined for legacy data).
 * @param key property key.
 * @returns the `YYYY-MM-DD` value, or undefined when unset/not a date.
 */
export function dateProperty(properties, key) {
    const property = properties?.[key];
    return property?.type === "date" ? property.value : undefined;
}
/**
 * Every set date from the given key vocabulary, earliest first. Reference
 * dates (purchasedOn) and unknown keys are excluded by the caller's key list.
 * Container support reuses this by passing `CONTAINER_DEADLINE_KEYS`.
 *
 * @param properties property bag.
 * @param keys deadline keys to consider.
 * @returns sorted deadline entries.
 */
export function deadlineEntries(properties, keys) {
    const entries = [];
    for (const key of keys) {
        const date = dateProperty(properties, key);
        if (date)
            entries.push({ key, date });
    }
    return entries.sort((a, b) => a.date.localeCompare(b.date) || a.key.localeCompare(b.key));
}
/** Every set item deadline, earliest first (purchasedOn never participates). */
export function itemDeadlineEntries(properties) {
    return deadlineEntries(properties, ITEM_DEADLINE_KEYS);
}
/** The soonest item deadline, or undefined when the item has none. */
export function earliestItemDeadline(properties) {
    return itemDeadlineEntries(properties)[0];
}
const STATUS_RANK = { later: 0, soon: 1, overdue: 2 };
/**
 * Whole days from today's local midnight to a day-precision date. Local
 * midnights keep "today" stable across the midnight-UTC boundary.
 *
 * @param iso `YYYY-MM-DD` date.
 * @param now reference instant (injectable for deterministic tests).
 * @returns negative when past, 0 today, positive when in the future.
 */
export function daysUntilDate(iso, now = new Date()) {
    const [year, month, day] = iso.split("-").map(Number);
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const target = new Date(year, month - 1, day);
    return Math.round((target.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
}
/**
 * Warning state of one deadline: red once past, amber within its own
 * threshold, neutral afterwards. Day precision — "today" is still soon.
 *
 * @param iso deadline date.
 * @param thresholdDays warning window in days.
 * @param now reference instant (injectable for deterministic tests).
 */
export function deadlineStatus(iso, thresholdDays, now = new Date()) {
    const days = daysUntilDate(iso, now);
    if (days < 0)
        return "overdue";
    if (days <= thresholdDays)
        return "soon";
    return "later";
}
/** Status of one item deadline under its own threshold (warranty = 30 days). */
export function itemDeadlineStatus(key, iso, now = new Date()) {
    return deadlineStatus(iso, itemDeadlineThresholdDays(key), now);
}
// ── Deadline filters (Overdue / Expiring soon) ───────────────────────
/** The two list filters ADR-0037 defines; both are threshold-driven. */
export const DEADLINE_FILTERS = ["overdue", "soon"];
export const ITEM_DEADLINE_RULES = {
    keys: ITEM_DEADLINE_KEYS,
    thresholdDays: (key) => itemDeadlineThresholdDays(key),
};
export const CONTAINER_DEADLINE_RULES = {
    keys: CONTAINER_DEADLINE_KEYS,
    thresholdDays: () => CONTAINER_DEADLINE_WARNING_DAYS,
};
// ── Urgency summaries ────────────────────────────────────────────────
/**
 * The worst status across an entity's deadlines — overdue beats soon beats
 * later. Used for the hidden-column row dot and the toolbar counts, where a
 * warranty inside its 30-day window must stay visible even when a later
 * best-before date sorts first.
 *
 * @param properties property bag.
 * @param rules deadline vocabulary and thresholds for the entity type.
 * @param now reference instant.
 * @returns the most urgent status, or undefined without any deadline.
 */
export function worstDeadlineStatus(properties, rules, now = new Date()) {
    let worst;
    for (const { key, date } of deadlineEntries(properties, rules.keys)) {
        const status = deadlineStatus(date, rules.thresholdDays(key), now);
        if (!worst || STATUS_RANK[status] > STATUS_RANK[worst])
            worst = status;
    }
    return worst;
}
/**
 * Count rows by their worst deadline status (overdue wins over soon). The same
 * function backs the global list's server-side counts and the client-side
 * per-container / browse lists, so the toolbar numbers cannot drift.
 *
 * @param entities rows carrying a property bag.
 * @param rules deadline vocabulary and thresholds for the entity type.
 * @param now reference instant (the server's clock for API counts).
 * @returns overdue and soon row counts.
 */
export function countDeadlineUrgencies(entities, rules = ITEM_DEADLINE_RULES, now = new Date()) {
    let overdue = 0;
    let soon = 0;
    for (const entity of entities) {
        const status = worstDeadlineStatus(entity.properties, rules, now);
        if (status === "overdue")
            overdue++;
        else if (status === "soon")
            soon++;
    }
    return { overdue, soon };
}
/** True for a filter token the lists understand. */
export function isDeadlineFilter(value) {
    return typeof value === "string" && DEADLINE_FILTERS.includes(value);
}
/**
 * Tolerant parse of the `deadline` query parameter. Repeatable values and
 * comma lists both work; unknown, empty and non-string values are dropped
 * (never an error) and duplicates collapse.
 *
 * @param value raw query value (`string`, repeated `string[]` or undefined).
 * @returns the known filters in request order.
 */
export function parseDeadlineFilters(value) {
    const raw = Array.isArray(value) ? value : [value];
    const filters = new Set();
    for (const entry of raw) {
        if (typeof entry !== "string")
            continue;
        for (const token of entry.split(",")) {
            const trimmed = token.trim();
            if (isDeadlineFilter(trimmed))
                filters.add(trimmed);
        }
    }
    return [...filters];
}
/**
 * True when any of the entity's deadlines matches one of the requested
 * filters. Overdue means strictly before today; soon means inside the key's
 * own warning window and not past. Filters combine as a union; reference dates
 * (purchasedOn) and unknown keys never participate. The single source of truth
 * for the server filter and the client-side list filters.
 *
 * @param properties item/container property bag.
 * @param filters requested filters; empty matches nothing.
 * @param rules deadline vocabulary and thresholds for the entity type.
 * @param now reference instant (injectable for deterministic tests).
 */
export function matchesDeadlineFilters(properties, filters, rules, now = new Date()) {
    if (filters.length === 0)
        return false;
    return deadlineEntries(properties, rules.keys).some(({ key, date }) => {
        const status = deadlineStatus(date, rules.thresholdDays(key), now);
        return status !== "later" && filters.includes(status);
    });
}
/** Item-list filter: four deadlines, warranty warning a month ahead. */
export function itemMatchesDeadlineFilters(properties, filters, now = new Date()) {
    return matchesDeadlineFilters(properties, filters, ITEM_DEADLINE_RULES, now);
}
/** Container-list filter: expires-on with a four-week window. */
export function containerMatchesDeadlineFilters(properties, filters, now = new Date()) {
    return matchesDeadlineFilters(properties, filters, CONTAINER_DEADLINE_RULES, now);
}
/**
 * "Soonest first" comparator over any dated entity: earliest deadline first,
 * unset rows last, name (then id) as the stable tie-break. Reference dates never
 * rank. Items and containers share this by passing their rule set.
 *
 * @param a left entity with at least a display name.
 * @param b right entity with at least a display name.
 * @param rules deadline vocabulary and thresholds for the entity type.
 * @returns negative when `a` sorts first.
 */
export function compareBySoonestDeadline(a, b, rules = ITEM_DEADLINE_RULES) {
    const left = deadlineEntries(a.properties, rules.keys)[0];
    const right = deadlineEntries(b.properties, rules.keys)[0];
    if (!left && !right)
        return compareByName(a, b);
    if (!left)
        return 1;
    if (!right)
        return -1;
    const byDate = left.date.localeCompare(right.date);
    return byDate !== 0 ? byDate : compareByName(a, b);
}
/**
 * Non-mutating "soonest first" sort over a row set; used by the per-container
 * and browse lists (the global item list sorts server-side so pagination stays
 * consistent).
 *
 * @param entities rows to sort.
 * @param rules deadline vocabulary and thresholds for the entity type.
 * @returns a new sorted array.
 */
export function sortBySoonestDeadline(entities, rules = ITEM_DEADLINE_RULES) {
    return [...entities].sort((a, b) => compareBySoonestDeadline(a, b, rules));
}
/**
 * Alphabetical comparator with id as the stable tie-break. Shared by the plain
 * name sort and the deadline comparator's tie-break.
 *
 * @param a left entity.
 * @param b right entity.
 * @returns negative when `a` sorts first.
 */
export function compareByName(a, b) {
    return a.name.localeCompare(b.name) || (a.id ?? "").localeCompare(b.id ?? "");
}
//# sourceMappingURL=properties.js.map